import { useEffect, useRef, useState } from 'react';
import type { StoredFile } from '@epasu/core';
import { X } from 'lucide-react';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatBytes } from '@/lib/utils';

interface Props {
  file: StoredFile;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Name, Beschreibung und Schlagworte einer abgelegten Datei.
 *
 * Der Inhalt lässt sich nicht ersetzen — dafür lädt man eine neue Datei hoch.
 * Eine stillschweigend ausgetauschte Datei unter gleichem Namen wäre der Grund
 * dafür, dass beim Kunden eine andere Preisliste ankommt als erwartet.
 */
export function FileEditDialog({ file, onClose, onSaved }: Props) {
  const [name, setName] = useState(file.name);
  const [description, setDescription] = useState(file.description ?? '');
  const [tags, setTags] = useState<string[]>(file.tags);
  const [neuesTag, setNeuesTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const tagHinzu = () => {
    const t = neuesTag.trim().toLowerCase();
    if (!t || tags.includes(t)) {
      setNeuesTag('');
      return;
    }
    setTags((l) => [...l, t]);
    setNeuesTag('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Der Name darf nicht leer sein.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await crm.files.update(file.id, {
        name: name.trim(),
        description: description.trim() || null,
        tags,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Datei bearbeiten"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-md rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">Datei bearbeiten</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {formatBytes(file.sizeBytes)}
          {file.mimeType ? ` · ${file.mimeType}` : ''}
        </p>

        <div className="mt-4 space-y-3">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Name</span>
            <Input ref={nameRef} value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Beschreibung
            </span>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Wofür ist die Datei?"
            />
          </label>

          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Schlagworte</span>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs"
                  >
                    {t}
                    <button
                      type="button"
                      aria-label={`${t} entfernen`}
                      onClick={() => setTags((l) => l.filter((x) => x !== t))}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={neuesTag}
                onChange={(e) => setNeuesTag(e.target.value)}
                placeholder="preise, flyer …"
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  tagHinzu();
                }}
              />
              <Button type="button" variant="outline" onClick={tagHinzu}>
                Hinzufügen
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Speichere …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
}
